import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DailyForecastExpandable from './weather/DailyForecastExpandable';
import { RainAlert } from './weather/RainAlert';
import RainWidget from './weather/RainWidget';
import { getRainSummaryFromHourly } from '../utils/weatherRainUtils';
import '../styles/components/WeatherModal.css';

const WeatherModal = ({ isOpen, onClose, parkCoordinates, parkName, isClosing }) => {
  const { t } = useTranslation();
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rainDataFromRainWidget, setRainDataFromRainWidget] = useState(null);
  const [bgClass, setBgClass] = useState('wm-bg-day-clear');
  const [weatherEffect, setWeatherEffect] = useState(null);
  const rainDropsRef = useRef([...Array(100)].map((_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    duration: `${0.5 + Math.random() * 0.5}s`,
    delay: `${Math.random() * 2}s`
  })));
  const snowFlakesRef = useRef([...Array(50)].map((_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    duration: `${3 + Math.random() * 5}s`,
    delay: `${Math.random() * 5}s`,
    opacity: Math.random()
  })));

  const currentLang = t('language') === 'English' ? 'en' : 'fr';
  const isFrenchPark = ((parkCoordinates?.country || '').toLowerCase() === 'france');

  const handleRainForecastUpdate = useCallback((rainForecastData) => {
    setRainDataFromRainWidget(rainForecastData);
  }, []);

  // Fonction pour obtenir la description météo traduite
  const getWeatherDescription = (code) => {
    const weatherCodeMap = {
      0: 'weather.codes.clearSky',
      1: 'weather.codes.mainlyClear',
      2: 'weather.codes.partlyCloudy',
      3: 'weather.codes.overcast',
      45: 'weather.codes.fog',
      48: 'weather.codes.depositingRimeFog',
      51: 'weather.codes.lightDrizzle',
      53: 'weather.codes.moderateDrizzle',
      55: 'weather.codes.denseDrizzle',
      56: 'weather.codes.lightFreezingDrizzle',
      57: 'weather.codes.denseFreezingDrizzle',
      61: 'weather.codes.slightRain',
      63: 'weather.codes.moderateRain',
      65: 'weather.codes.heavyRain',
      66: 'weather.codes.lightFreezingRain',
      67: 'weather.codes.heavyFreezingRain',
      71: 'weather.codes.slightSnow',
      73: 'weather.codes.moderateSnow',
      75: 'weather.codes.heavySnow',
      77: 'weather.codes.snowGrains',
      80: 'weather.codes.slightRainShowers',
      81: 'weather.codes.moderateRainShowers',
      82: 'weather.codes.violentRainShowers',
      85: 'weather.codes.slightSnowShowers',
      86: 'weather.codes.heavySnowShowers',
      95: 'weather.codes.thunderstorm',
      96: 'weather.codes.thunderstormWithSlightHail',
      99: 'weather.codes.thunderstormWithHeavyHail'
    };
    
    return weatherCodeMap[code] ? t(weatherCodeMap[code]) : t('weather.codes.weatherConditions');
  };

  // Codes météo simplifiés
  const WEATHER_CODES = {
    0: { icon: '☀️' },
    1: { icon: '🌤️' },
    2: { icon: '⛅' },
    3: { icon: '☁️' },
    45: { icon: '🌫️' },
    48: { icon: '🌫️' },
    51: { icon: '🌦️' },
    53: { icon: '🌦️' },
    55: { icon: '🌦️' },
    56: { icon: '🌨️' },
    57: { icon: '🌨️' },
    61: { icon: '🌧️' },
    63: { icon: '🌧️' },
    65: { icon: '🌧️' },
    66: { icon: '❄️' },
    67: { icon: '❄️' },
    71: { icon: '🌨️' },
    73: { icon: '🌨️' },
    75: { icon: '🌨️' },
    77: { icon: '❄️' },
    81: { icon: '🌦️' },
    82: { icon: '🌦️' },
    85: { icon: '🌨️' },
    86: { icon: '❄️' },
    95: { icon: '⛈️' },
    96: { icon: '⛈️' },
    99: { icon: '⛈️' }
  };

  // Fonction pour récupérer les données météo via l'API backend
  const fetchWeatherData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Utiliser les coordonnées de config.js avec le format lat/lng
      const coords = parkCoordinates;
      if (!coords) {
        throw new Error(t('weather.coordinatesNotFound'));
      }
      
      // Adapter le format: parkCoordinates peut avoir latitude/longitude OU lat/lng
      const latitude = coords.latitude || coords.lat;
      const longitude = coords.longitude || coords.lng;
      
      if (!latitude || !longitude) {
        throw new Error(t('weather.invalidCoordinates'));
      }
      
      // Déterminer l'URL de base selon l'environnement
      const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
        ? 'https://api-internal.magicparktimes.com' 
        : 'http://localhost:3001');
      
      const params = new URLSearchParams({
        lat: latitude,
        lon: longitude,
        lang: currentLang
      });
      
      const response = await fetch(
        `${API_BASE_URL}/api/weather-enhanced?${params}`
      );

      if (!response.ok) {
        throw new Error(`${t('weather.apiError')}: ${response.status}`);
      }

      const data = await response.json();
      setWeatherData(data);
    } catch (err) {
      console.error(t('weather.weatherError') + ':', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [parkCoordinates, t, currentLang]);

  // Charger les données quand le modal s'ouvre
  useEffect(() => {
    if (isOpen && parkCoordinates) {
      fetchWeatherData();
    }
  }, [isOpen, parkCoordinates, fetchWeatherData]);

  // Calcul du background dynamique selon le weather_code
  useEffect(() => {
    if (!weatherData?.current) return;

    const code = weatherData.current.weather_code;
    const hour = new Date().getHours();
    const isDay = hour >= 6 && hour < 21;

    let newBgClass = isDay ? 'wm-bg-day-clear' : 'wm-bg-night-clear';
    let newEffect = null;

    if (
      (code >= 51 && code <= 67) ||
      (code >= 80 && code <= 82) ||
      (code >= 95 && code <= 99)
    ) {
      newBgClass = isDay ? 'wm-bg-day-rain' : 'wm-bg-night-rain';
      newEffect = 'rain';
    } else if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) {
      newBgClass = isDay ? 'wm-bg-day-snow' : 'wm-bg-night-snow';
      newEffect = 'snow';
    } else if (code === 1 || code === 2) {
      newBgClass = isDay ? 'wm-bg-day-partly-cloudy' : 'wm-bg-night-cloudy';
    } else if (code === 3 || code === 45 || code === 48) {
      newBgClass = isDay ? 'wm-bg-day-cloudy' : 'wm-bg-night-cloudy';
    }

    setBgClass(newBgClass);
    setWeatherEffect(newEffect);
  }, [weatherData]);

  // Calcul des données de pluie
  const rainData = useMemo(() => {
    const summary = getRainSummaryFromHourly(weatherData?.hourly);
    if (!summary) return null;

    return {
      probability: summary.probability,
      probabilityMax: summary.probabilityMax,
      amount: summary.amount,
      amountMax: summary.amountMax,
      willRain: summary.willRain,
      isSnow: summary.isSnow,
      source: 'openmeteo'
    };
  }, [weatherData]);

  const effectiveRainData = useMemo(() => {
    return (isFrenchPark && rainDataFromRainWidget) ? rainDataFromRainWidget : rainData;
  }, [isFrenchPark, rainDataFromRainWidget, rainData]);

  // Générer un résumé météo dynamique basé sur la structure Open-Meteo
  const generateWeatherSummary = () => {
    if (!weatherData?.current) return '';
    
    const temp = Math.round(weatherData.current.temperature_2m);
    const feelsLike = Math.round(weatherData.current.apparent_temperature);
    const code = weatherData.current.weather_code;
    const weatherDesc = getWeatherDescription(code);
    const humidity = weatherData.current.relative_humidity_2m;
    
    // Vérifier s'il va pleuvoir dans les prochaines heures (6 prochaines heures)
    const nextHours = weatherData.hourly?.precipitation_probability?.slice(0, 6) || [];
    const maxRainProb = Math.max(...nextHours.filter(p => p !== undefined));
    const willRain = maxRainProb > 50;
    
    let summary = `${t('weather.currently')} ${temp}°C (${t('weather.feelsLike').toLowerCase()} ${feelsLike}°C) ${weatherDesc}.`;
    
    if (willRain) {
      summary += ` ${t('weather.riskOfRain')} ${maxRainProb}% ${t('weather.inNextHours')}.`;
    }
    
    if (humidity > 80) {
      summary += ` ${t('weather.highHumidity')} (${humidity}%).`;
    }
    
    return summary;
  };

  // Gestion du scroll de la page et détection de la barre d'adresse
  useEffect(() => {
    if (isOpen) {
      // Bloquer le scroll de la page
      document.body.style.overflow = 'hidden';

      // Détection de la barre d'adresse mobile
      const detectMobileBrowserBar = () => {
        const viewportHeight = window.innerHeight;
        const visualViewportHeight = window.visualViewport?.height || viewportHeight;

        // Si la barre d'adresse est présente, la visual viewport height sera plus petite
        if (window.visualViewport && viewportHeight > visualViewportHeight + 100) {
          document.body.classList.add('mobile-browser-bar-present');
        } else {
          document.body.classList.remove('mobile-browser-bar-present');
        }
      };

      // Détection initiale
      detectMobileBrowserBar();

      // Écouter les changements de la visual viewport (barre d'adresse qui apparaît/disparaît)
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', detectMobileBrowserBar);
      }

      // Nettoyage
      return () => {
        document.body.style.overflow = 'unset';
        document.body.classList.remove('mobile-browser-bar-present');
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', detectMobileBrowserBar);
        }
      };
    } else {
      // Restaurer le scroll et nettoyer les classes
      document.body.style.overflow = 'unset';
      document.body.classList.remove('mobile-browser-bar-present');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className="weather-modal-overlay" 
      onClick={(e) => {
        // Fermer si on clique sur l'overlay (pas sur le contenu)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClose();
        }
      }}
    >
      <div 
        className={`weather-modal-content ${isClosing ? 'closing' : ''} ${bgClass}`} 
        onClick={(e) => e.stopPropagation()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {weatherEffect === 'rain' && (
          <div className="wm-weather-effects-container" aria-hidden="true">
            {rainDropsRef.current.map((drop) => (
              <div key={drop.id} className="wm-rain-drop" style={{
                left: drop.left,
                animationDuration: drop.duration,
                animationDelay: drop.delay
              }} />
            ))}
          </div>
        )}
        {weatherEffect === 'snow' && (
          <div className="wm-weather-effects-container" aria-hidden="true">
            {snowFlakesRef.current.map((flake) => (
              <div key={flake.id} className="wm-snow-flake" style={{
                left: flake.left,
                animationDuration: flake.duration,
                animationDelay: flake.delay,
                opacity: flake.opacity
              }}>❄</div>
            ))}
          </div>
        )}
        <div className="weather-modal-header">
          <button className="weather-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="weather-modal-body">
          {loading && (
            <div className="weather-loading">
              <div className="loading-spinner"></div>
              <p>{t('weather.loading')}</p>
            </div>
          )}

          {error && (
            <div className="weather-error">
              <p>{t('weather.error')}: {error}</p>
              <button onClick={fetchWeatherData} className="retry-button">
                🔄 {t('weather.retry')}
              </button>
            </div>
          )}

          {weatherData && !loading && (
            <div className="weather-content">
              {/* Header principal style iOS */}
              <div className="weather-main-info">
                <div className="weather-park-name">
                  {parkName}
                </div>
                <div className="weather-icon">
                  {WEATHER_CODES[weatherData.current?.weather_code]?.icon || '🌤️'}
                </div>
                <div className="temperature-main">
                  {Math.round(weatherData.current?.temperature_2m || 0)}°C
                </div>
              </div>

              {/* Alerte de pluie (Open‑Meteo par défaut, RainWidget pour la France) */}
              <RainAlert
                rainData={effectiveRainData}
                language={currentLang}
              />

              {/* Widget de pluie avec graphique (France uniquement) */}
              {((parkCoordinates?.country || '').toLowerCase() === 'france') && (
                <RainWidget
                  lat={parkCoordinates?.latitude || parkCoordinates?.lat}
                  lon={parkCoordinates?.longitude || parkCoordinates?.lng}
                  isFrenchPark={true}
                  onRainForecastUpdate={handleRainForecastUpdate}
                />
              )}

              {/* Conditions détaillées compactes - Générées dynamiquement */}
              <div className="weather-summary">
                <div className="summary-text">
                  {generateWeatherSummary()}
                </div>
              </div>

              {/* Prévisions horaires style iOS */}
              <div className="hourly-forecast-ios">
                <div className="forecast-header">
                  <span className="forecast-icon">🕐</span>
                  <span className="forecast-title">{t('weather.hourlyForecast')}</span>
                </div>
                <div className="hourly-scroll-container">
                  {(() => {
                    if (!weatherData.hourly?.time) return [];
                    
                    // Trouver l'index de l'heure actuelle ou de la prochaine heure
                    const now = new Date();
                    const hourlyTimes = weatherData.hourly.time.map(time => new Date(time));
                    
                    let startIndex = hourlyTimes.findIndex(time => time > now);
                    if (startIndex === -1) startIndex = 0;
                    
                    // Extraire les 24 prochaines heures
                    const endIndex = Math.min(startIndex + 24, hourlyTimes.length);
                    const nextHours = weatherData.hourly.time.slice(startIndex, endIndex);
                    
                    return nextHours.map((time, relativeIndex) => {
                      const hour = new Date(time);
                      const absoluteIndex = startIndex + relativeIndex;
                      const temp = weatherData.hourly.temperature_2m?.[absoluteIndex];
                      const rainProb = weatherData.hourly.precipitation_probability?.[absoluteIndex];
                      const code = weatherData.hourly.weather_code?.[absoluteIndex];
                      
                      // Affichage spécial pour "Maintenant"
                      const timeDisplay = relativeIndex === 0 ? t('weather.now') :
                        hour.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                      
                      return (
                        <div key={absoluteIndex} className="hourly-item-ios">
                          <div className="hourly-time-ios">
                            {timeDisplay}
                          </div>
                          <div className="hourly-icon-ios">
                            {WEATHER_CODES[code]?.icon || '🌤️'}
                          </div>
                          {rainProb > 0 && (
                            <div className="hourly-rain-ios">
                              {rainProb}%
                            </div>
                          )}
                          <div className="hourly-temp-ios">
                            {temp ? Math.round(temp) : '--'}°
                          </div>
                        </div>
                      );
                    });
                  })()
                  }
                </div>
              </div>

              {/* Prévisions sur 10 jours style iOS - Expandable */}
              <DailyForecastExpandable 
                weatherData={weatherData}
                currentLang={currentLang}
              />

              {/* Détails météo */}
              <div className="weather-details-section">
                <div className="detail-item">
                  <span className="detail-label">{t('weather.humidity')}</span>
                  <span className="detail-value">{weatherData.current?.relative_humidity_2m || 0}%</span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">{t('weather.wind')}</span>
                  <span className="detail-value">
                    {weatherData.current?.wind_speed_10m ? `${Math.round(weatherData.current.wind_speed_10m)} km/h` : t('common.notAvailable')}
                  </span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">{t('weather.direction')}</span>
                  <span className="detail-value">
                    {(() => {
                      const degrees = weatherData.current?.wind_direction_10m;
                      if (typeof degrees !== 'number' || isNaN(degrees)) return t('common.notAvailable');
                      const directions = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
                      const index = Math.round(degrees / 45) % 8;
                      return `${directions[index]} (${degrees}°)`;
                    })()
                    }
                  </span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">{t('weather.updated')}</span>
                  <span className="detail-value">
                    {new Date().toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="weather-modal-footer">
          <div>
            {t('weather.dataProvider')} <a
              href="https://open-meteo.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Open-Meteo
            </a>
          </div>
          <div style={{ marginTop: '3px' }}>
            {t('weather.updateFrequency')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherModal;
